/**
 * Cross-tab payment-cart lock ownership.
 *
 * Session identifiers stay in tab-scoped sessionStorage. A matching nonce in
 * localStorage plus an exclusive Web Lock let other tabs observe the lock
 * without releasing it. Crash/restart: Web Lock is released by the browser, so
 * a later tab can reconcile. No wall-clock expiry.
 */

export const PAYMENT_CART_WEB_LOCK_NAME = "gmf-payment-cart-lock"
export const PAYMENT_LOCK_OWNER_STORAGE_KEY = "gmf-payment-lock-owner"
export const PAYMENT_LOCK_OWNER_TOKEN_KEY = "gmf-payment-lock-owner-token"
/** Storefront base (`/r/{slug}` or "") that owns the origin-global lock marker. */
export const PAYMENT_LOCK_STOREFRONT_KEY = "gmf-payment-lock-storefront"

type HeldLockQuery = () => Promise<string[]>

let lockAbort: AbortController | null = null
let heldLockQueryOverride: HeldLockQuery | null = null

function randomOwnerNonce(): string {
  try {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID()
    }
  } catch {
    /* ignore */
  }
  return `gmf-${Math.random().toString(36).slice(2)}${Math.random().toString(36).slice(2)}`
}

function readSessionToken(): string | null {
  try {
    const v = sessionStorage.getItem(PAYMENT_LOCK_OWNER_TOKEN_KEY)
    return v?.trim() ? v.trim() : null
  } catch {
    return null
  }
}

function readStoredOwner(): string | null {
  try {
    const v = localStorage.getItem(PAYMENT_LOCK_OWNER_STORAGE_KEY)
    return v?.trim() ? v.trim() : null
  } catch {
    return null
  }
}

function writeOwnerNonce(nonce: string, storefrontBasePath: string): void {
  try {
    sessionStorage.setItem(PAYMENT_LOCK_OWNER_TOKEN_KEY, nonce)
  } catch {
    /* private mode */
  }
  try {
    localStorage.setItem(PAYMENT_LOCK_OWNER_STORAGE_KEY, nonce)
    localStorage.setItem(PAYMENT_LOCK_STOREFRONT_KEY, storefrontBasePath)
  } catch {
    /* private mode */
  }
}

function clearOwnerNonce(): void {
  try {
    sessionStorage.removeItem(PAYMENT_LOCK_OWNER_TOKEN_KEY)
  } catch {
    /* ignore */
  }
  try {
    localStorage.removeItem(PAYMENT_LOCK_OWNER_STORAGE_KEY)
    localStorage.removeItem(PAYMENT_LOCK_STOREFRONT_KEY)
  } catch {
    /* ignore */
  }
}

export function readPaymentLockStorefront(): string | null {
  try {
    const v = localStorage.getItem(PAYMENT_LOCK_STOREFRONT_KEY)
    return v == null ? null : v
  } catch {
    return null
  }
}

function canQueryWebLocks(): boolean {
  return (
    heldLockQueryOverride != null ||
    (typeof navigator !== "undefined" && typeof navigator.locks?.query === "function")
  )
}

async function queryHeldLockNames(): Promise<string[]> {
  if (heldLockQueryOverride) {
    return heldLockQueryOverride()
  }
  if (typeof navigator === "undefined" || typeof navigator.locks?.query !== "function") {
    return []
  }
  try {
    const state = await navigator.locks.query()
    return (state.held ?? [])
      .map((lock) => String(lock.name ?? ""))
      .filter((name) => name.length > 0)
  } catch {
    return []
  }
}

function acquireWebLock(): void {
  if (typeof navigator === "undefined" || typeof navigator.locks?.request !== "function") {
    return
  }
  if (lockAbort) return
  const abort = new AbortController()
  lockAbort = abort
  void navigator.locks
    .request(
      PAYMENT_CART_WEB_LOCK_NAME,
      { mode: "exclusive", signal: abort.signal },
      () => new Promise<void>(() => { /* held until abort or tab close */ })
    )
    .catch(() => {
      if (lockAbort === abort) lockAbort = null
    })
}

function abortWebLock(): void {
  lockAbort?.abort()
  lockAbort = null
}

/** True when this tab's session token matches the shared owner nonce. */
export function thisTabOwnsPaymentLock(): boolean {
  const token = readSessionToken()
  const stored = readStoredOwner()
  return Boolean(token && stored && token === stored)
}

/** Shared owner marker exists, but this tab is not the owner. */
export function hasForeignPaymentLockOwner(): boolean {
  const stored = readStoredOwner()
  if (!stored) return false
  return !thisTabOwnsPaymentLock()
}

/**
 * Sync guard for cart unlock / clear. True if this tab has a session token match
 * or another tab left a live owner marker.
 */
export function shouldPreservePaymentCartLock(): boolean {
  return thisTabOwnsPaymentLock() || hasForeignPaymentLockOwner()
}

/** Another tab still holds the exclusive Web Lock (or conservative fallback). */
export async function isPaymentLockHeldByLiveOwner(): Promise<boolean> {
  if (thisTabOwnsPaymentLock()) return true
  if (!hasForeignPaymentLockOwner()) return false
  if (canQueryWebLocks()) {
    const names = await queryHeldLockNames()
    return names.includes(PAYMENT_CART_WEB_LOCK_NAME)
  }
  // No Web Locks: do not let a sibling tab steal; crash recovery is degraded.
  return true
}

/** Persist + claim ownership for the current payment session tab. */
export function claimPaymentLockOwnership(storefrontBasePath: string = ""): void {
  const existing = readSessionToken()
  const nonce = existing && thisTabOwnsPaymentLock() ? existing : randomOwnerNonce()
  writeOwnerNonce(nonce, storefrontBasePath)
  acquireWebLock()
}

/** Drop owner markers and release the Web Lock. Safe when this tab is not owner. */
export function releasePaymentLockOwnership(): void {
  abortWebLock()
  clearOwnerNonce()
}

/** Test-only: mock navigator.locks.query held names. */
export function setPaymentLockHeldQueryForTests(query: HeldLockQuery | null): void {
  heldLockQueryOverride = query
}

/** Test-only isolation. */
export function resetPaymentLockOwnershipForTests(): void {
  abortWebLock()
  clearOwnerNonce()
  heldLockQueryOverride = null
}
